/**
 * MOLR product-page enhancements (module)
 * ---------------------------------------
 * Two progressive enhancements, both no-ops without JS:
 *
 *   1. Complete-your-set add-to-cart. Each bundle row and the "add the set"
 *      control is a real Shopify add-to-cart form (it works with no JS by
 *      submitting to /cart/add and landing on the cart). Here we intercept it
 *      and add over AJAX instead, reusing Horizon's own CartLinesUpdateEvent
 *      so the cart drawer / header count update in place — exactly the path
 *      product-form.js uses. Any failure falls back to a native form submit,
 *      so the add can never be lost.
 *
 *   2. Social-proof placement. A band set to "above the fold" is hoisted to sit
 *      directly beneath the buy module. With no JS it renders in document
 *      order, so it is still shown — just where it was placed.
 *
 * Skipped in the theme editor (design mode re-renders sections and would fight
 * both behaviours). Styles live in assets/molr.css §31–§32.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

(() => {
  'use strict';

  if (document.documentElement.classList.contains('shopify-design-mode')) return;

  /* ======================================================================
     1. Complete-your-set — AJAX add reusing Horizon's cart contract
     ====================================================================== */

  /** Collect the section ids of every cart-items component so the add response
   *  carries the re-rendered drawer markup Horizon needs to morph. */
  function cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids.join(',');
  }

  /** Read {id, quantity} lines out of a bundle form's hidden inputs, whether it
   *  carries a single `id`/`quantity` pair or a batch of `items[n][id]`. */
  function linesFromForm(form) {
    const single = form.querySelector('input[name="id"]');
    if (single) {
      const qty = form.querySelector('input[name="quantity"]');
      return [{ id: single.value, quantity: parseInt(qty && qty.value, 10) || 1 }];
    }
    const byIndex = new Map();
    form.querySelectorAll('input[name^="items["]').forEach((input) => {
      const match = input.name.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (!match) return;
      const [, index, key] = match;
      const line = byIndex.get(index) || { id: '', quantity: 1 };
      line[key === 'quantity' ? 'quantity' : 'id'] = key === 'quantity' ? parseInt(input.value, 10) || 1 : input.value;
      byIndex.set(index, line);
    });
    return Array.from(byIndex.values()).filter((line) => line.id);
  }

  async function ajaxAdd(form, button) {
    const lines = linesFromForm(form);
    if (!lines.length) throw new Error('No lines to add');

    // Optimistic event — lets any listener react before the round-trip lands.
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: line.id, quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const addRes = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lines.map((line) => ({ id: line.id, quantity: line.quantity })),
          sections: cartSectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      const added = await addRes.json();
      if (added.status || added.status === 422) {
        deferred.reject(added);
        throw new Error(added.description || added.message || 'Add to cart failed');
      }

      // Pull the authoritative cart, then resolve the event with the drawer
      // sections from the add response — the shape Horizon's listeners expect.
      const cartRes = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'molr-complete-set',
          sections: added.sections,
          didError: false,
        },
      });

      if (button) confirmButton(button);
      return true;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }

  /** Flash the row's "Add" button into a confirmed state, then restore it. */
  function confirmButton(button) {
    button.classList.add('is-added');
    button.disabled = true;
    window.setTimeout(() => {
      button.classList.remove('is-added');
      button.disabled = false;
    }, 1800);
  }

  function initBundleAdd() {
    const forms = document.querySelectorAll('[data-molr-add], [data-molr-add-all]');
    forms.forEach((form) => {
      form.addEventListener('submit', (event) => {
        const button = form.querySelector('[data-molr-add-btn], button[type="submit"]');
        event.preventDefault();
        ajaxAdd(form, button).catch(() => {
          // Anything went wrong (offline, unexpected response): honour the add
          // by letting the real form post, so the item is never silently lost.
          form.submit();
        });
      });
    });
  }

  /* ======================================================================
     2. Social-proof placement — hoist an "above the fold" band under the buy
     ====================================================================== */

  function initProofFold() {
    const band = document.querySelector('[data-molr-proof-fold="above"]');
    if (!band) return;
    const bandSection = band.closest('.shopify-section');
    const mainSection = document.getElementById('shopify-section-main');
    if (!bandSection || !mainSection || bandSection.previousElementSibling === mainSection) return;
    mainSection.insertAdjacentElement('afterend', bandSection);
  }

  /* ====================================================================== */

  function init() {
    initBundleAdd();
    initProofFold();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
